import React, { useState, useEffect, useMemo } from 'react';
import { X, Image, FileText, Plus, Trash2 } from 'lucide-react';
import Select, { StylesConfig, OptionProps, components, CSSObjectWithLabel, ControlProps, OptionProps as ReactSelectOptionProps } from 'react-select';
import { useQueryClient } from 'react-query';
import { Tour, TourType, TourTypeLabels } from '../../types';
import Button from '../UI/Button';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { languages } from '../../constants/Languages';

type OptionType = {
  value: number | 'add_new';
  label: string;
};

interface TourFormProps {
  tour?: Tour | null;
  onSave: (tourData: Partial<Tour>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const TourForm: React.FC<TourFormProps> = ({ tour, onSave, onCancel, isLoading = false }) => {
  const { currentFlavourId } = useAuth();
  const queryClient = useQueryClient();
  
  const [tourName, setTourName] = useState<Array<{ language: string; text: string }>>([{ language: '', text: '' }]);
  const [tourDescription, setTourDescription] = useState<Array<{ language: string; text: string }>>([{ language: '', text: '' }]);

  const [formData, setFormData] = useState<Partial<Tour>>({
    name: '',
    type: TourType.WALKING,
    description: '',
    duration: '2 hours',
    step: '10 stops',
    distance: '5 km',
    retailPrice: '0',
    sellingPrice: '0',
    place: '',
    status: 'active',
    isDeleted: false,
    flavourId: currentFlavourId || '',
    imageUrl: '',
    thumbnailUrl: '',
    workMode: 'paid',
    narrationPoints: '',
    tourDirectionJsonUrl: '',
    productId: undefined,
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [directionJsonFile, setDirectionJsonFile] = useState<File | null>(null);

  const [previewStops, setPreviewStops] = useState(2);
  const [isPaid, setIsPaid] = useState(true);

  const [countries, setCountries] = useState<Array<{ id: number; name: string; code?: string }>>([]);
  const [places, setPlaces] = useState<Array<{ id: number; name: string; countryId: number }>>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<number | ''>('');
  const [selectedPlaceId, setSelectedPlaceId] = useState<number | ''>('');
  
  const [showAddCountryModal, setShowAddCountryModal] = useState(false);
  const [showAddPlaceModal, setShowAddPlaceModal] = useState(false);
  const [newCountryName, setNewCountryName] = useState('');
  const [newPlaceName, setNewPlaceName] = useState('');
  const [isCreatingCountry, setIsCreatingCountry] = useState(false);
  const [isCreatingPlace, setIsCreatingPlace] = useState(false);
  
  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState<{
    show: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    message: '',
    onConfirm: () => {},
  });
  
  // Loading state for language deletion
  const [isRemovingLanguage, setIsRemovingLanguage] = useState(false);

  useEffect(() => {
    if (tour) {
      setFormData({
        ...tour,
        workMode: tour.workMode || 'paid',
      });
      setIsPaid(tour.workMode !== 'free');
      
      if (tour.name) {
        try {
          const nameParsed = typeof tour.name === 'string' ? JSON.parse(tour.name) : tour.name;
          if (Array.isArray(nameParsed) && nameParsed.length > 0) {
            setTourName(nameParsed);
          } else {
            setTourName([{ language: 'en', text: typeof tour.name === 'string' ? tour.name : JSON.stringify(tour.name) }]);
          }
        } catch {
          setTourName([{ language: 'en', text: tour.name }]);
        }
      } else {
        setTourName([{ language: '', text: '' }]);
      }
      
      if (tour.description) {
        try {
          const descParsed = typeof tour.description === 'string' ? JSON.parse(tour.description) : tour.description;
          if (Array.isArray(descParsed) && descParsed.length > 0) {
            setTourDescription(descParsed);
          } else {
            setTourDescription([{ language: 'en', text: typeof tour.description === 'string' ? tour.description : JSON.stringify(tour.description) }]);
          }
        } catch {
          setTourDescription([{ language: 'en', text: tour.description }]);
        }
      } else {
        setTourDescription([{ language: '', text: '' }]);
      }
    } else {
      setTourName([{ language: '', text: '' }]);
      setTourDescription([{ language: '', text: '' }]);
    }
  }, [tour]);

  useEffect(() => {
    if (currentFlavourId) {
      setFormData(prev => ({
        ...prev,
        flavourId: currentFlavourId,
      }));
    }
  }, [currentFlavourId]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await apiService.getCountries();
        setCountries(data);
      } catch (error) {
        toast.error('Failed to load countries');
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchPlaces = async () => {
      if (selectedCountryId) {
        try {
          const data = await apiService.getPlaces(selectedCountryId as number);
          setPlaces(data);
        } catch (error) {
          toast.error('Failed to load places');
        }
      } else {
        setPlaces([]);
        setSelectedPlaceId('');
      }
    };
    fetchPlaces();
  }, [selectedCountryId]);

  useEffect(() => {
    if (tour) {
      if (tour.countryId) {
        setSelectedCountryId(tour.countryId);
      }
      if (tour.placeId) {
        setSelectedPlaceId(tour.placeId);
      }
    }
  }, [tour]);

  useEffect(() => {
    if (selectedPlaceId && places.length > 0) {
      const selectedPlace = places.find(p => p.id === selectedPlaceId);
      if (selectedPlace && formData.place !== selectedPlace.name) {
        setFormData(prev => ({
          ...prev,
          place: selectedPlace.name,
        }));
      }
    }
  }, [selectedPlaceId, places]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const countryOptions = useMemo<OptionType[]>(() => {
    const options: OptionType[] = countries.map(country => ({
      value: country.id,
      label: country.name,
    }));
    options.push({ value: 'add_new', label: '+ Add New Country' });
    return options;
  }, [countries]);

  const placeOptions = useMemo<OptionType[]>(() => {
    const options: OptionType[] = places.map(place => ({
      value: place.id,
      label: place.name,
    }));
    if (selectedCountryId) {
      options.push({ value: 'add_new', label: '+ Add New Place' });
    }
    return options;
  }, [places, selectedCountryId]);

  const Option = (props: OptionProps<OptionType>) => {
    const isAddNew = props.data.value === 'add_new';
    return (
      <components.Option
        {...props}
        className={isAddNew ? 'text-blue-600 font-semibold' : ''}
      >
        {props.children}
      </components.Option>
    );
  };

  const selectStyles: StylesConfig<OptionType, false> = {
    control: (base: CSSObjectWithLabel, state: ControlProps<OptionType, false>) => ({
      ...base,
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
      },
    }),
    option: (base: CSSObjectWithLabel, state: ReactSelectOptionProps<OptionType, false>) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#3b82f6'
        : state.isFocused
        ? '#eff6ff'
        : 'white',
      color: state.isSelected ? 'white' : state.data?.value === 'add_new' ? '#2563eb' : '#111827',
      fontWeight: state.data?.value === 'add_new' ? '600' : 'normal',
    }),
  };

  const handleCountryChange = (selectedOption: OptionType | null) => {
    if (!selectedOption) {
      setSelectedCountryId('');
      setFormData(prev => ({
        ...prev,
        countryId: undefined,
        placeId: undefined,
      }));
      setSelectedPlaceId('');
      return;
    }

    if (selectedOption.value === 'add_new') {
      setShowAddCountryModal(true);
    } else {
      const countryId = selectedOption.value as number;
      setSelectedCountryId(countryId);
      setFormData(prev => ({
        ...prev,
        countryId: countryId,
        placeId: undefined,
      }));
      setSelectedPlaceId('');
    }
  };

  const handlePlaceChange = (selectedOption: OptionType | null) => {
    if (!selectedOption) {
      setSelectedPlaceId('');
      setFormData(prev => ({
        ...prev,
        placeId: undefined,
        place: '',
      }));
      return;
    }

    if (selectedOption.value === 'add_new') {
      if (!selectedCountryId) {
        toast.error('Please select a country first');
        return;
      }
      setShowAddPlaceModal(true);
    } else {
      const placeId = selectedOption.value as number;
      setSelectedPlaceId(placeId);
      const selectedPlace = places.find(p => p.id === placeId);
      setFormData(prev => ({
        ...prev,
        placeId: placeId,
        place: selectedPlace ? selectedPlace.name : '',
      }));
    }
  };

  const handleCreateCountry = async () => {
    if (!newCountryName.trim()) {
      toast.error('Please enter a country name');
      return;
    }

    setIsCreatingCountry(true);
    try {
      const newCountry = await apiService.createCountry({ name: newCountryName.trim() });
      setCountries(prev => [...prev, newCountry].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedCountryId(newCountry.id);
      setFormData(prev => ({
        ...prev,
        countryId: newCountry.id,
      }));
      setNewCountryName('');
      setShowAddCountryModal(false);
      toast.success('Country created successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create country');
    } finally {
      setIsCreatingCountry(false);
    }
  };

  const handleCreatePlace = async () => {
    if (!newPlaceName.trim()) {
      toast.error('Please enter a place name');
      return;
    }

    if (!selectedCountryId) {
      toast.error('Please select a country first');
      return;
    }

    setIsCreatingPlace(true);
    try {
      const newPlace = await apiService.createPlace({
        name: newPlaceName.trim(),
        countryId: selectedCountryId as number,
      });
      setPlaces(prev => [...prev, newPlace].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedPlaceId(newPlace.id);
      setFormData(prev => ({
        ...prev,
        placeId: newPlace.id,
        place: newPlace.name,
      }));
      setNewPlaceName('');
      setShowAddPlaceModal(false);
      toast.success('Place created successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create place');
    } finally {
      setIsCreatingPlace(false);
    }
  };

  const handleFileChange = (field: string, file: File | null) => {
    switch (field) {
      case 'image':
        setImageFile(file);
        break;
      case 'thumbnail':
        setThumbnailFile(file);
        break;
      case 'directionJson':
        setDirectionJsonFile(file);
        break;
    }
  };

  const handleAddLanguage = () => {
    const lastName = tourName[tourName.length - 1];
    const lastDesc = tourDescription[tourDescription.length - 1];
    
    if ((lastName && !lastName.language && !lastName.text) || 
        (lastDesc && !lastDesc.language && !lastDesc.text)) {
      toast.error('Please fill in the current language entries before adding another');
      return;
    }
    
    setTourName([...tourName, { language: '', text: '' }]);
    setTourDescription([...tourDescription, { language: '', text: '' }]);
  };

  const handleRemoveLanguageClick = (index: number) => {
    if (tourName.length <= 1) {
      toast.error('At least one language entry is required');
      return;
    }
    
    const languageToRemove = tourName[index]?.language || tourDescription[index]?.language;
    const languageName = languages.find((l: any) => l.code === languageToRemove)?.name || languageToRemove || 'this language';
    
    setConfirmationDialog({
      show: true,
      message: `Are you sure you want to remove ${languageName}? This will also remove ${languageName} from all related tour stops.`,
      onConfirm: () => {
        setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
        handleRemoveLanguage(index);
      },
    });
  };

  const handleRemoveLanguage = async (index: number) => {
    // Get the language code that's being removed
    const languageToRemove = tourName[index]?.language || tourDescription[index]?.language;
    
    // New name/description after removing this language (used for UI and for update tour API)
    const newName = tourName.filter((_, i) => i !== index);
    const newDesc = tourDescription.filter((_, i) => i !== index);
    
    if (!languageToRemove) {
      // If no language is selected, just remove from UI
      setTourName(newName);
      setTourDescription(newDesc);
      return;
    }
    
    // If we're editing an existing tour, remove language from tour stops and update tour
    if (tour?.id && languageToRemove) {
      setIsRemovingLanguage(true);
      try {
        toast.loading('Removing language from tour stops...', { id: 'remove-language' });
        
        // Fetch all tour stops for this tour
        const tourStops = await apiService.getTourPoints(tour.id);
        
        // Helper function to remove language from JSON array
        const removeLanguageFromArray = (jsonString: string | null | undefined): { updated: string; changed: boolean } => {
          if (!jsonString) return { updated: '[]', changed: false };
          try {
            const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
            if (Array.isArray(parsed)) {
              const filtered = parsed.filter((item: any) => item.language !== languageToRemove);
              if (filtered.length !== parsed.length) {
                return { updated: JSON.stringify(filtered), changed: true };
              }
            }
            return { updated: typeof jsonString === 'string' ? jsonString : JSON.stringify(parsed), changed: false };
          } catch {
            return { updated: typeof jsonString === 'string' ? jsonString : '[]', changed: false };
          }
        };
        
        // Process each tour stop to remove the language
        let updatedCount = 0;
        for (const stop of tourStops) {
          if (!stop.id) continue;
          
          const updateData = new FormData();
          let hasChanges = false;
          
          // Remove language from name
          if (stop.name) {
            const result = removeLanguageFromArray(stop.name);
            if (result.changed) {
              updateData.append('name', result.updated);
              hasChanges = true;
            }
          }
          
          // Remove language from description
          if (stop.discription) {
            const result = removeLanguageFromArray(stop.discription);
            if (result.changed) {
              updateData.append('discription', result.updated);
              hasChanges = true;
            }
          }
          
          // Remove language from mainAudioUrl
          if (stop.mainAudioUrl) {
            const result = removeLanguageFromArray(stop.mainAudioUrl);
            if (result.changed) {
              updateData.append('mainAudioUrl', result.updated);
              hasChanges = true;
            }
          }
          
          // Remove language from audioUrl (if exists)
          if (stop.audioUrl) {
            const result = removeLanguageFromArray(stop.audioUrl);
            if (result.changed) {
              updateData.append('audioUrl', result.updated);
              hasChanges = true;
            }
          }
          
          // Remove language from stopLanguagesMap
          if (stop.stopLanguagesMap) {
            const result = removeLanguageFromArray(stop.stopLanguagesMap);
            if (result.changed) {
              updateData.append('stopLanguagesMap', result.updated);
              hasChanges = true;
            }
          }
          
          // Remove language from stopLanguagesPreviewMap
          if (stop.stopLanguagesPreviewMap) {
            const result = removeLanguageFromArray(stop.stopLanguagesPreviewMap);
            if (result.changed) {
              updateData.append('stopLanguagesPreviewMap', result.updated);
              hasChanges = true;
            }
          }
          
          // Only update if there were changes
          if (hasChanges) {
            try {
              await apiService.updateTourPoint(tour.id, stop.id, updateData);
              updatedCount++;
            } catch (error) {
              // Continue with other stops even if one fails
            }
          }
        }
        
        // Update tour name/description on the server so it's saved even if admin doesn't click "Update Tour"
        const namePayload = newName.filter((item) => item.text !== undefined && item.language);
        const descPayload = newDesc.filter((item) => item.text !== undefined && item.language);
        let tourUpdateOk = true;
        try {
          // updateTour stringifies name/description when they are arrays; Tour type declares string
          await apiService.updateTour(tour.id, {
            name: namePayload,
            description: descPayload,
          } as any);
        } catch (error) {
          tourUpdateOk = false;
          toast.error('Language removed from stops but tour update failed. Click "Update Tour" to save.', { id: 'remove-language' });
        }
        if (tourUpdateOk) {
          queryClient.invalidateQueries(['tours']);
          if (updatedCount > 0) {
            toast.success(`Language removed from tour and ${updatedCount} tour stop(s). Tour saved.`, { id: 'remove-language' });
          } else {
            toast.success('Language removed from tour. Tour saved.', { id: 'remove-language' });
          }
        }
      } catch (error) {
        toast.error('Failed to remove language from some tour stops', { id: 'remove-language' });
      } finally {
        setIsRemovingLanguage(false);
      }
    } else {
      setIsRemovingLanguage(false);
    }
    
    // Remove from UI
    setTourName(newName);
    setTourDescription(newDesc);
  };

  const handleSelectLanguage = (index: number, language: string) => {
    // Check if language is already selected in another entry
    if (language && language.trim() !== '') {
      const isDuplicate = tourName.some((item, i) => 
        i !== index && item.language === language && item.language.trim() !== ''
      );
      
      if (isDuplicate) {
        const languageName = languages.find((l: any) => l.code === language)?.name || language;
        toast.error(`${languageName} is already selected. Please choose a different language.`);
        return;
      }
    }
    
    const updatedName = tourName.map((item, i) => 
      i === index ? { ...item, language } : item
    );
    const updatedDesc = tourDescription.map((item, i) => 
      i === index ? { ...item, language } : item
    );
    
    setTourName(updatedName);
    setTourDescription(updatedDesc);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCountryId) {
      toast.error('Please select a country');
      return;
    }
    
    if (!selectedPlaceId) {
      toast.error('Please select a place');
      return;
    }
    
    // Check for duplicate languages
    const selectedLanguages = tourName
      .map(item => item.language)
      .filter(lang => lang && lang.trim() !== '');
    
    const duplicateLanguages = selectedLanguages.filter((lang, index) => 
      selectedLanguages.indexOf(lang) !== index
    );
    
    if (duplicateLanguages.length > 0) {
      // Get unique duplicates without using Set
      const uniqueDuplicates: string[] = [];
      duplicateLanguages.forEach(lang => {
        if (!uniqueDuplicates.includes(lang)) {
          uniqueDuplicates.push(lang);
        }
      });
      
      const languageNames = uniqueDuplicates.map(lang => {
        const langObj = languages.find((l: any) => l.code === lang);
        return langObj?.name || lang;
      }).join(', ');
      toast.error(`Duplicate languages detected: ${languageNames}. Please select different languages for each entry.`);
      return;
    }
    
    const validNameEntries = tourName.filter(item => item.text && item.language);
    const validDescEntries = tourDescription.filter(item => item.text && item.language);
    
    if (validNameEntries.length === 0) {
      toast.error('Please fill in at least one language for tour name');
      return;
    }
    
    if (validDescEntries.length === 0) {
      toast.error('Please fill in at least one language for tour description');
      return;
    }
    
    const requiredFields = [
      'productId',
      'duration',
      'step',
      'sellingPrice',
      'distance', 
      'narrationPoints',
    ];
    
    const missingFields = requiredFields.filter(field => !formData[field as keyof typeof formData]);
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    if (!imageFile && !formData.imageUrl) {
      toast.error('Please upload a tour main image');
      return;
    }
    
    if (!thumbnailFile && !formData.thumbnailUrl) {
      toast.error('Please upload a tour thumbnail image');
      return;
    }

    // Validate retail price must be greater than selling price when both are provided
    const retailVal = formData.retailPrice != null && formData.retailPrice !== '' ? parseFloat(String(formData.retailPrice)) : NaN;
    const sellingVal = formData.sellingPrice != null && formData.sellingPrice !== '' ? parseFloat(String(formData.sellingPrice)) : NaN;
    if (!isNaN(retailVal) && !isNaN(sellingVal) && retailVal <= sellingVal) {
      toast.error('Retail price must be greater than selling price');
      return;
    }
    
    const selectedPlace = places.find(p => p.id === selectedPlaceId);
    const placeName = selectedPlace ? selectedPlace.name : formData.place || '';

    const toNumberOrUndefined = (value: number | ''): number | undefined => {
      if (value === '' || value === null || value === undefined) {
        return undefined;
      }
      return typeof value === 'number' ? value : Number(value);
    };

    const nameArray = validNameEntries.map(item => ({ text: item.text, language: item.language }));
    const descriptionArray = validDescEntries.map(item => ({ text: item.text, language: item.language }));

    const tourData = {
      ...formData,
      name: nameArray,
      description: descriptionArray,
      workMode: isPaid ? 'paid' : 'free',
      step: `${previewStops} stops`,
      isDeleted: false,
      status: formData.status || 'active',
      productId: formData.productId || undefined,
      countryId: toNumberOrUndefined(selectedCountryId),
      placeId: toNumberOrUndefined(selectedPlaceId),
      place: placeName,
      image: imageFile,
      thumbnail: thumbnailFile,
      directionJson: directionJsonFile,
    };

    onSave(tourData as any);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {tour ? 'Edit Tour' : 'Create New Tour'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Tour Information (Multilingual)</h3>
            
            {tourName.map((_, langIndex: number) => {
              const currentLanguage = tourName[langIndex]?.language || 
                                     tourDescription[langIndex]?.language || "";
              
              return (
                <div key={langIndex} className="bg-white border border-slate-300 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b">
                    <span className="text-sm text-gray-500 font-medium">{langIndex + 1}.</span>
                    <select
                      value={currentLanguage}
                      onChange={(e) => handleSelectLanguage(langIndex, e.target.value)}
                      className="bg-white border border-slate-300 text-gray-900 text-sm rounded-lg p-2 outline-none min-w-[150px] focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Language</option>
                      {languages?.map((lang: any) => {
                        // Check if this language is already selected in another entry
                        const isAlreadySelected = tourName.some((item, i) => 
                          i !== langIndex && item.language === lang.code && item.language.trim() !== ''
                        );
                        
                        return (
                          <option 
                            key={lang.code} 
                            value={lang.code}
                            disabled={isAlreadySelected}
                            style={isAlreadySelected ? { color: '#9ca3af', fontStyle: 'italic' } : {}}
                          >
                            {lang.name}{isAlreadySelected ? ' (already selected)' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {tourName.length > 1 && (
                      <button
                        onClick={() => handleRemoveLanguageClick(langIndex)}
                        type="button"
                        disabled={isRemovingLanguage}
                        className={`ml-auto p-2 rounded focus:outline-none transition-colors ${
                          isRemovingLanguage 
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                            : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                        }`}
                        title={isRemovingLanguage ? "Removing language..." : "Remove language"}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tour Name *
                    </label>
                    <input
                      type="text"
                      disabled={!currentLanguage || currentLanguage.trim() === ''}
                      className={`border rounded-md border-slate-300 p-2 w-full text-sm ${
                        !currentLanguage || currentLanguage.trim() === ''
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white'
                      }`}
                      value={tourName[langIndex]?.text || ""}
                      onChange={(e) => {
                        const updated = tourName.map((item, i) =>
                          i === langIndex ? { ...item, text: e.target.value } : item
                        );
                        setTourName(updated);
                      }}
                      placeholder={!currentLanguage || currentLanguage.trim() === '' ? "Please select a language first" : "Enter tour name"}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      disabled={!currentLanguage || currentLanguage.trim() === ''}
                      className={`border rounded-md border-slate-300 p-2 w-full text-sm ${
                        !currentLanguage || currentLanguage.trim() === ''
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white'
                      }`}
                      value={tourDescription[langIndex]?.text || ""}
                      onChange={(e) => {
                        const updated = tourDescription.map((item, i) =>
                          i === langIndex ? { ...item, text: e.target.value } : item
                        );
                        setTourDescription(updated);
                      }}
                      placeholder={!currentLanguage || currentLanguage.trim() === '' ? "Please select a language first" : "Enter tour description"}
                      rows={4}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="flex justify-center pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleAddLanguage}
              className="flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add More Language</span>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country *
              </label>
              <Select
                value={selectedCountryId ? countryOptions.find(opt => opt.value === selectedCountryId) : null}
                onChange={handleCountryChange}
                options={countryOptions}
                placeholder="Search or select country..."
                isSearchable
                isClearable
                components={{ Option }}
                styles={selectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Place/Location *
              </label>
              <Select
                value={selectedPlaceId ? placeOptions.find(opt => opt.value === selectedPlaceId) : null}
                onChange={handlePlaceChange}
                options={placeOptions}
                placeholder={selectedCountryId ? "Search or select place..." : "Please select a country first"}
                isSearchable
                isClearable
                isDisabled={!selectedCountryId}
                components={{ Option }}
                styles={{
                  ...selectStyles,
                  control: (base: CSSObjectWithLabel, state: ControlProps<OptionType, false>) => ({
                    ...base,
                    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                    backgroundColor: state.isDisabled ? '#f3f4f6' : 'white',
                    cursor: state.isDisabled ? 'not-allowed' : 'default',
                    '&:hover': {
                      borderColor: state.isFocused ? '#3b82f6' : state.isDisabled ? '#d1d5db' : '#9ca3af',
                    },
                  }),
                }}
                className="react-select-container"
                classNamePrefix="react-select"
              />
              {!selectedCountryId && (
                <p className="text-xs text-gray-500 mt-1">
                  Please select a country first
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tour Type *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              >
                <option value={TourType.WALKING}>{TourTypeLabels[TourType.WALKING]}</option>
                <option value={TourType.DRIVING}>{TourTypeLabels[TourType.DRIVING]}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration *
              </label>
              <input
                type="text"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="e.g., 2 hours"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Distance *
              </label>
              <input
                type="text"
                name="distance"
                value={formData.distance}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="e.g., 3.2 km"
              />
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Car Rental Per Person *
              </label>
              <input
                type="text"
                name="carRentalPerPerson"
                value={formData.carRentalPerPerson}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="0.00"
              />
            </div> */}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Retail Price
              </label>
              <input
                type="text"
                name="retailPrice"
                value={formData.retailPrice}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="50.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selling Price *
              </label>
              <input
                type="text"
                name="sellingPrice"
                value={formData.sellingPrice}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="35.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product ID *
              </label>
              <input
                type="text"
                name="productId"
                value={formData.productId || ''}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="e.g., com.example.tour"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Narration Points *
              </label>
              <input
                type="text"
                name="narrationPoints"
                value={formData.narrationPoints}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="e.g., 15"
              />
            </div>
          </div>

          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Booking Email Template *
            </label>
            <textarea
              name="bookingEmailTemplate"
              value={formData.bookingEmailTemplate}
              onChange={handleInputChange}
              required
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter booking email template"
            />
          </div> */}

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tour Main Image</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tour Main Image *
              </label>
              <div className="text-center">
                {(formData.imageUrl || imageFile) ? (
                  <div className="mb-4">
                    <img 
                      src={imageFile ? URL.createObjectURL(imageFile) : formData.imageUrl} 
                      alt="Tour main image" 
                      className="w-48 h-36 object-cover rounded mx-auto mb-2"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmationDialog({
                          show: true,
                          message: 'Are you sure you want to remove the tour main image?',
                          onConfirm: () => {
                            setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
                            setFormData(prev => ({ ...prev, imageUrl: '' }));
                            setImageFile(null);
                          },
                        });
                      }}
                      className="text-red-600 text-sm hover:text-red-700"
                    >
                      Remove Image
                    </button>
                  </div>
                ) : (
                  <label htmlFor="tour-main-image" className="cursor-pointer block">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 hover:bg-gray-50 transition-colors">
                      <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-1">
                        Click to upload tour main image
                      </p>
                      <p className="text-xs text-gray-500">
                        This image will be displayed as the main tour image in the app
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        handleFileChange('image', file);
                      }}
                      className="hidden"
                      id="tour-main-image"
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tour Thumbnail</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tour Thumbnail Image *
              </label>
              <div className="text-center">
                {(formData.thumbnailUrl || thumbnailFile) ? (
                  <div className="mb-4">
                    <img 
                      src={thumbnailFile ? URL.createObjectURL(thumbnailFile) : formData.thumbnailUrl} 
                      alt="Tour thumbnail" 
                      className="w-32 h-24 object-cover rounded mx-auto mb-2"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmationDialog({
                          show: true,
                          message: 'Are you sure you want to remove the tour thumbnail image?',
                          onConfirm: () => {
                            setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
                            setFormData(prev => ({ ...prev, thumbnailUrl: '' }));
                            setThumbnailFile(null);
                          },
                        });
                      }}
                      className="text-red-600 text-sm hover:text-red-700"
                    >
                      Remove Image
                    </button>
                  </div>
                ) : (
                  <label htmlFor="tour-thumbnail" className="cursor-pointer block">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 hover:bg-gray-50 transition-colors">
                      <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-1">
                        Click to upload tour thumbnail image
                      </p>
                      <p className="text-xs text-gray-500">
                        This image will be displayed as the tour thumbnail in the app
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        handleFileChange('thumbnail', file);
                      }}
                      className="hidden"
                      id="tour-thumbnail"
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tour Settings</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview Stops (for paid tours)
                </label>
                <select
                  value={previewStops}
                  onChange={(e) => setPreviewStops(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value={1}>1 Stop</option>
                  <option value={2}>2 Stops</option>
                  <option value={3}>3 Stops</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Number of stops available for preview in paid tours
                </p>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : (tour ? 'Update Tour' : 'Create Tour')}
            </Button>
          </div>
        </form>

        {showAddCountryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Add New Country
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country Name *
                </label>
                <input
                  type="text"
                  value={newCountryName}
                  onChange={(e) => setNewCountryName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Enter country name"
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddCountryModal(false);
                    setNewCountryName('');
                  }}
                  disabled={isCreatingCountry}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleCreateCountry}
                  disabled={isCreatingCountry || !newCountryName.trim()}
                >
                  {isCreatingCountry ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {showAddPlaceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Add New Place
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Place Name *
                </label>
                <input
                  type="text"
                  value={newPlaceName}
                  onChange={(e) => setNewPlaceName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Enter place name"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Country: {countries.find(c => c.id === selectedCountryId)?.name || 'N/A'}
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddPlaceModal(false);
                    setNewPlaceName('');
                  }}
                  disabled={isCreatingPlace}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleCreatePlace}
                  disabled={isCreatingPlace || !newPlaceName.trim()}
                >
                  {isCreatingPlace ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {confirmationDialog.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirm Delete
              </h3>
              <p className="text-sm text-gray-700 mb-6">
                {confirmationDialog.message}
              </p>
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setConfirmationDialog({ show: false, message: '', onConfirm: () => {} });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={confirmationDialog.onConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TourForm;
